'use client'
import React, { FormEvent, useState, ReactElement } from 'react'
import { useRouter } from 'next/navigation'
import Card from '@/components/controls/Card'
import Input from '@/components/controls/Input'
import Button from '@/components/controls/Button'
import Message, { MessageProps } from '@/components/controls/Message'
import { setForCustomerId } from '@/lib/sessionStorage'
import logger from '@/lib/utils/logger'

const LoginForm = ({showReset, redirectTo}: {showReset: (message?: MessageProps) => void, redirectTo: string}): ReactElement => {
  const [message, setMessage] = useState<MessageProps | null>(null)
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const router = useRouter()

  if (!redirectTo) {
    redirectTo = '/'
  }

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setMessage(null)

    try {
      const loginResponse = await fetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          username: username,
          password: password
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json().catch(() => ({}))
        if (errorData.message === 'Password reset required') {
          showReset({ type: 'info', children: 'You must reset your password before logging in.' })
        }
        setMessage({ 
          type: 'error', 
          timeout: 7000,
          afterTimeout: () => setMessage(null),
          children: errorData.message || 'Login failed. Please check your credentials and try again.'
        })
        setPassword('')
        setIsSubmitting(false)
      } else {
        const data = await loginResponse.json()
        setForCustomerId(data.user.customerId)
        if (data.user.active == true) {
          router.push(redirectTo)
        } else {
          router.push('/signup')
        }
      }
    } catch (error) {
      logger.log('Login error:', error)
      setMessage({ 
        type: 'error', 
        children: 'An unexpected error occurred. Please try again later.',
        timeout: 7000,
        afterTimeout: () => setMessage(null)
      })
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <form onSubmit={handleLogin}>
        <Input type="text" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Email or Account #" />
        <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Password" autoComplete="off" />
        { message && 
          <Message {...message} />
        }
        <Button className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-400 hover:to-blue-400 active:from-green-800 active:to-blue-800 text-white font-bold" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in...' : 'Log In'}
        </Button>
      </form>
      <div className="text-center mt-4">
        <a className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800 cursor-pointer" onClick={() => showReset()}>
          Forgot Password?
        </a>
      </div>
    </Card>
  )
}

export default LoginForm